// ═══════════════════════════════════════════════════════════════════
// CategoryShowcase (Apr28 v10) — single horizontal scroll row
// ═══════════════════════════════════════════════════════════════════
// Was a 3-column grid in v7 with 4:5 portrait tiles which cropped
// landscape category banners on the sides ("Bags & Accessories"
// overlay text getting clipped). v10 fixes both issues:
//
//   • Single horizontal row that scrolls — no more vertical space taken
//   • 4:3 frame ratio — matches your landscape banner photos
//   • Tiles ~260px wide on desktop, ~200px on mobile
//   • Desktop: arrow buttons ‹ › appear when there's more to scroll
//   • Mobile: swipe naturally (same UX as your subcategory circles)
//   • Right-edge fade gradient hints at more content
//   • No auto-scroll (categories aren't time-sensitive)
//   • No pagination dots (clean — just the fade hint)
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cldCard } from '../../utils/cloudinary';

const CAT_ICONS = {
  jewelry: '💎',
  bangles: '💍',
  sarees: '🥻',
  hairAccessories: '🌸',
  bags: '👜',
  garments: '👗',
  girls: '🎀',
  seasonal: '🌟',
};

const CategoryShowcase = ({ categories, isMobile, isSmall }) => {
  const { i18n } = useTranslation();
  const hi = i18n.language === 'hi';
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Track scroll position so arrows + fades can hide at start/end
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth + 8 < el.scrollWidth);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [categories]);

  const scrollByDirection = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = isMobile ? 220 : 290; // ~one tile width + gap
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (!categories || categories.length === 0) return null;

  return (
    <section style={S.section}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.eyebrow}>✦ {hi ? 'श्रेणियाँ' : 'Categories'} ✦</div>
        <h2 style={{ ...S.title, fontSize: isMobile ? 24 : 34 }}>
          {hi ? (
            <span>कैटेगरी से <em style={S.titleEm}>खोजें</em></span>
          ) : (
            <span>Shop by <em style={S.titleEm}>Category</em></span>
          )}
        </h2>

        {/* Decorative gold diamond divider */}
        <div style={S.divider}>
          <span style={S.dividerLine} />
          <span style={S.dividerDiamond}>◆</span>
          <span style={S.dividerLine} />
        </div>
      </div>

      {/* Scroll row container — relative wrapper for arrow buttons + fade */}
      <div style={S.scrollWrap}>
        {/* Left arrow — desktop only, only when scrollable left */}
        {!isMobile && canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByDirection(-1)}
            style={{ ...S.arrowBtn, left: 8 }}
            aria-label="Scroll left"
          >
            ‹
          </button>
        )}

        {/* The scroller */}
        <div
          ref={scrollRef}
          style={{
            ...S.scroller,
            padding: isMobile ? '6px 14px 18px' : '6px 32px 22px',
            gap: isMobile ? 12 : 16,
          }}
          className="gg-cat-row"
        >
          {categories.map(cat => (
            <CategoryTile
              key={cat._id || cat.slug}
              cat={cat}
              hi={hi}
              isMobile={isMobile}
              isSmall={isSmall}
            />
          ))}
        </div>

        {/* Right arrow — desktop only, only when more to the right */}
        {!isMobile && canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByDirection(1)}
            style={{ ...S.arrowBtn, right: 8 }}
            aria-label="Scroll right"
          >
            ›
          </button>
        )}

        {/* Right fade — hints there's more if scrollable right */}
        {canScrollRight && <div style={{ ...S.rightFade, width: isMobile ? 32 : 56 }} />}
        {/* Left fade — only after scrolling past start */}
        {canScrollLeft && <div style={{ ...S.leftFade, width: isMobile ? 32 : 56 }} />}
      </div>
    </section>
  );
};

// ─── Single tile ────────────────────────────────────────────────
const CategoryTile = ({ cat, hi, isMobile, isSmall }) => {
  const [hovered, setHovered] = useState(false);

  const slug = cat.slug || (cat.name || '').toLowerCase().replace(/\s+/g, '-');
  const label = hi && cat.nameHi ? cat.nameHi : cat.name;
  const fallbackIcon = CAT_ICONS[cat.slug] || CAT_ICONS[slug] || '✨';

  // Tile width — smaller on phones, generous on desktop
  // ~3.7 visible on a 1280 desktop = "more on the right" feel
  const tileWidth = isSmall ? 170 : isMobile ? 200 : 260;

  return (
    <Link
      to={`/shop?category=${encodeURIComponent(slug)}`}
      style={{
        textDecoration: 'none',
        flex: '0 0 auto',
        width: tileWidth,
        scrollSnapAlign: 'start',
        display: 'block',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          ...S.tile,
          ...(hovered ? S.tileHover : {}),
        }}
      >
        {/* Image / icon area — 4:3 LANDSCAPE (no more side cropping!) */}
        <div style={S.imgWrap}>
          {/* Top shimmer line */}
          <div
            style={{
              ...S.shimmer,
              background: hovered
                ? 'linear-gradient(90deg, transparent, rgba(240,208,128,0.95), transparent)'
                : 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)',
            }}
          />

          {cat.image ? (
            <img
              src={cldCard(cat.image)}
              alt={label}
              style={{
                ...S.img,
                transform: hovered ? 'scale(1.06)' : 'scale(1)',
              }}
              loading="lazy"
              draggable={false}
              onError={e => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div style={S.iconFallback}>{fallbackIcon}</div>
          )}

          {/* Gold radial halo on hover */}
          <div
            style={{
              ...S.halo,
              opacity: hovered ? 1 : 0,
            }}
          />

          {/* Sparkles on hover */}
          {hovered && (
            <>
              <span style={{ ...S.sparkle, top: '14%', right: '14%', fontSize: 14 }}>✦</span>
              <span style={{ ...S.sparkle, top: '24%', left: '16%', fontSize: 11 }}>✧</span>
              <span style={{ ...S.sparkle, bottom: '40%', right: '22%', fontSize: 9 }}>✦</span>
            </>
          )}

          {/* Bottom gradient overlay — keeps text readable on any photo */}
          <div style={S.overlay} />

          {/* Text content positioned at bottom */}
          <div style={S.textContent}>
            <h3
              style={{
                ...S.tileName,
                fontSize: isMobile ? 13 : 15,
              }}
            >
              {label}
            </h3>
            <div
              style={{
                ...S.cta,
                color: hovered ? '#f0d080' : 'rgba(201,168,76,0.85)',
              }}
            >
              {hi ? 'अभी खरीदें' : 'Shop Now'}
              <span style={{ ...S.ctaArrow, transform: hovered ? 'translateX(4px)' : 'translateX(0)' }}>
                →
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// ─── Styles ──────────────────────────────────────────────────────
const S = {
  section: { padding: '36px 0 28px' },

  header: {
    textAlign: 'center',
    marginBottom: 22,
    padding: '0 20px',
  },
  eyebrow: {
    color: 'rgba(201,168,76,0.7)',
    fontSize: 11,
    letterSpacing: 4,
    textTransform: 'uppercase',
    fontFamily: 'system-ui',
    marginBottom: 12,
    fontWeight: 600,
  },
  title: {
    color: '#f5f0e8',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontWeight: 700,
    margin: '0 0 16px',
    lineHeight: 1.15,
    letterSpacing: '-0.5px',
  },
  titleEm: {
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #c9a84c 0%, #f0d080 50%, #c9a84c 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
  },
  dividerLine: {
    display: 'inline-block',
    width: 70,
    height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)',
  },
  dividerDiamond: { color: '#c9a84c', fontSize: 11 },

  // Scroll wrapper — relative for arrow positioning + fade overlays
  scrollWrap: {
    position: 'relative',
    maxWidth: 1280,
    margin: '0 auto',
  },

  // The actual scrolling element
  scroller: {
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollSnapType: 'x proximity',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(201,168,76,0.25) transparent',
  },

  // Desktop arrow buttons
  arrowBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(201,168,76,0.95), rgba(240,208,128,0.95))',
    color: '#04020a',
    border: 'none',
    fontSize: 26,
    fontWeight: 700,
    fontFamily: 'system-ui',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 6px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)',
    zIndex: 10,
    transition: 'transform 0.15s ease, box-shadow 0.2s ease',
    lineHeight: 1,
    paddingBottom: 4, // optical center for the chevron
  },

  rightFade: {
    position: 'absolute',
    top: 0,
    bottom: 22,
    right: 0,
    background: 'linear-gradient(to left, var(--bg, #04020a) 0%, transparent 100%)',
    pointerEvents: 'none',
    zIndex: 5,
  },
  leftFade: {
    position: 'absolute',
    top: 0,
    bottom: 22,
    left: 0,
    background: 'linear-gradient(to right, var(--bg, #04020a) 0%, transparent 100%)',
    pointerEvents: 'none',
    zIndex: 5,
  },

  // Tile
  tile: {
    background: 'linear-gradient(180deg, #0e0b18 0%, #100d1f 100%)',
    border: '1px solid rgba(201,168,76,0.18)',
    borderRadius: 16,
    overflow: 'hidden',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
    transform: 'translateY(0)',
  },
  tileHover: {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(201,168,76,0.55)',
    boxShadow:
      '0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,168,76,0.25), 0 0 30px rgba(201,168,76,0.15)',
  },

  // 4:3 LANDSCAPE — matches your category banner photos, no edge cropping
  imgWrap: {
    aspectRatio: '4 / 3',
    background: 'linear-gradient(135deg, #2a1f1a 0%, #1a1424 100%)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'flex-end',
  },
  shimmer: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 1,
    pointerEvents: 'none',
    transition: 'background 0.4s ease',
    zIndex: 2,
  },
  img: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  iconFallback: {
    position: 'absolute',
    top: '38%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 64,
    opacity: 0.85,
    filter: 'drop-shadow(0 4px 14px rgba(201,168,76,0.4))',
    pointerEvents: 'none',
  },
  halo: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.18) 0%, transparent 65%)',
    pointerEvents: 'none',
    transition: 'opacity 0.4s ease',
  },
  sparkle: {
    position: 'absolute',
    color: 'rgba(255,255,255,0.7)',
    pointerEvents: 'none',
    zIndex: 3,
  },
  overlay: {
    position: 'absolute',
    bottom: 0, left: 0, right: 0,
    height: '55%',
    background:
      'linear-gradient(to top, rgba(10,8,16,0.95) 0%, rgba(10,8,16,0.55) 50%, transparent 100%)',
    pointerEvents: 'none',
  },
  textContent: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    padding: '12px 14px 14px',
    textAlign: 'center',
  },
  tileName: {
    color: '#f5f0e8',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontWeight: 600,
    margin: 0,
    lineHeight: 1.3,
    letterSpacing: '0.2px',
    textShadow: '0 2px 12px rgba(0,0,0,0.6)',
  },
  cta: {
    marginTop: 4,
    fontSize: 9,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    fontWeight: 700,
    fontFamily: 'system-ui',
    transition: 'color 0.25s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  ctaArrow: {
    display: 'inline-block',
    transition: 'transform 0.3s ease',
  },
};

export default CategoryShowcase;
